import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import logo from '../../../assets/logo.png'
import { useAuthStore } from '../store/authStore'

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordPage(): JSX.Element {
  const forgotPassword = useAuthStore((s) => s.forgotPassword)
  const [serverError, setServerError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const isDev = import.meta.env.DEV
  const resetBaseUrl = import.meta.env.VITE_PORTAL_RESET_PASSWORD_URL as string | undefined

  let resetBaseOrigin = ''
  try {
    resetBaseOrigin = resetBaseUrl ? new URL(resetBaseUrl).origin : ''
  } catch {
    resetBaseOrigin = ''
  }

  const showDevResetWarning =
    isDev && !!resetBaseOrigin && resetBaseOrigin !== window.location.origin

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  const onSubmit = async (formData: ForgotPasswordFormData): Promise<void> => {
    setServerError('')
    setSuccessMessage('')
    const result = await forgotPassword(formData.email)

    if (result.success) {
      setSuccessMessage(
        result.message ||
          'If an account with that email exists, a password reset link has been sent.',
      )
      return
    }

    setServerError(result.error || 'Unable to process request.')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src={logo} alt="QuickComm" className="h-10 w-auto" />
        </div>

        <div className="rounded-xl border border-[#E5E7EB] bg-white p-8 shadow-sm">
          <h1 className="mb-1 text-[28px] font-bold leading-tight text-gray-900">
            Forgot password
          </h1>
          <p className="mb-6 text-[14px] text-gray-500">
            Enter your email and we will send a reset link.
          </p>

          {showDevResetWarning && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-700">
              Reset emails are configured for <strong>{resetBaseOrigin}</strong>, but you are running on{' '}
              <strong>{window.location.origin}</strong>. For local testing, set{' '}
              <code>VITE_PORTAL_RESET_PASSWORD_URL</code> in backend config to your local reset URL.
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-[13px] font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                {...register('email')}
                className="h-10 w-full rounded-lg border border-[#E5E7EB] bg-white px-4 text-[14px] text-gray-900 outline-none transition-colors focus:border-brand-green"
                placeholder="you@company.com"
              />
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
            </div>

            {successMessage && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                <p className="text-[13px] text-green-700">{successMessage}</p>
              </div>
            )}

            {serverError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[13px] text-red-600">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="h-10 w-full rounded-lg bg-brand-green text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Sending...' : 'Send reset link'}
            </button>

            <p className="text-center text-[13px] text-gray-500">
              Remembered your password?{' '}
              <Link to="/login" className="font-medium text-brand-green hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
